import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { get, isAuthenticated } from "./services/api";
import { useUI } from "./UIContext";
import { handleErr } from "./utils/functions";

const UserContext = createContext();
const USER_STORAGE_KEY = 'user_data';

function getStoredUser() {
    try {
        const rawUser = localStorage.getItem(USER_STORAGE_KEY);
        return rawUser ? JSON.parse(rawUser) : undefined;
    } catch (err) {
        localStorage.removeItem(USER_STORAGE_KEY);
        return undefined;
    }
}

function storeUser(user) {
    if (!user) {
        localStorage.removeItem(USER_STORAGE_KEY);
        return;
    }

    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function UserProvider({ children }) {
    const [user, setUserState] = useState(() => getStoredUser());
    const { setLoading, setMessage } = useUI();

    const setUser = useCallback((userData) => {
        setUserState(userData);
        storeUser(userData);
    }, []);

    const clearUser = useCallback(() => {
        setUser(undefined);
    }, [setUser]);

    const refreshUserOnAuthenticate = useCallback(async () => {
        if (!isAuthenticated()) {
            clearUser();
            return;
        }

        setLoading(true);

        await get('/user', true, setMessage,
            (response) => {
                setUser(response.data);
            },
            (err) => {
                handleErr(setMessage, `Falha ao obter os dados do utilizador: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }, [clearUser, setLoading, setMessage, setUser]);

    useEffect(() => {
        function onAuthTokenChanged() {
            if (!isAuthenticated()) {
                clearUser();
                return;
            }

            refreshUserOnAuthenticate();
        }

        window.addEventListener('auth-token-changed', onAuthTokenChanged);

        if (!isAuthenticated()) {
            clearUser();
        }

        return () => {
            window.removeEventListener('auth-token-changed', onAuthTokenChanged);
        };
    }, [clearUser, refreshUserOnAuthenticate]);

    const contextValue = useMemo(() => ({
        user,
        setUser,
        clearUser,
        refreshUserOnAuthenticate,
    }), [user, setUser, clearUser, refreshUserOnAuthenticate]);

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    return useContext(UserContext);
}